// The building blocks of the Capabilities page (./CapabilitySetupPage.tsx),
// shared by a team's view and the org admin's Development and Standards sets
// (./SharedSetView.tsx): the tokens, the setup stepper, the team switcher, the
// add form, Elliott's note, a capability's row and the role's header.

import React, { useEffect, useState } from 'react';
import { Box, Collapse, IconButton, InputBase, ListSubheader, Menu, MenuItem, Typography } from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import { Bone, fadeInSx } from '../../../../components/ui/DashboardSkeletons';
import ElliottWorking from '../../../../components/ui/ElliottWorking';
import DosenTooltip from '../../../../components/ui/DosenTooltip';
import { LEVEL_COLOR, LEVEL_NAME } from '../Program/components/learning-lifecycle/CapabilityGapsList';
import { LEVEL_ORDER, type CapabilityLevel } from '../Program/components/learning-lifecycle/teamCapability';
import {
  canDeleteRole,
  levelsSet,
  MAX_CRITERIA,
  MAX_NAME,
  type SetupCapability,
  type SetupRole,
  type SetupStep
} from './capabilitySetup';
import { ActionButton } from './designSystem';
import { levelProblems } from './levelRule';
import type { CapabilitySource } from './capabilitySource';
import SourceChip from './SourceChip';
import { MOBILE_SX, useIsMobileApp } from '../../../../mobile/mobileApp';
export const FONT = 'Inter, sans-serif';
export const BRANDY = '#F79420';
export const DONE = '#00972D';
export const TEXT = '#FDFDFD';
export const TEXT_2 = '#B6B6B6';
export const TEXT_3 = '#868686';
export const LINE = '#2A2A2A';
export const SURFACE = '#1C1C1C';
export const BRANDY_FRAME = '1px solid rgba(247, 148, 32, 0.45)';
export const BRANDY_TINT = 'rgba(247, 148, 32, 0.06)';

export const LABEL = {
  fontFamily: FONT,
  fontSize: 10.5,
  fontWeight: 700,
  letterSpacing: '0.08em',
  lineHeight: 1,
  textTransform: 'uppercase' as const,
  color: TEXT_3
};
export const BODY = { fontFamily: FONT, fontSize: 13, lineHeight: 1.55, color: TEXT_2 };

export const SECTION_TITLE = {
  flexShrink: 0,
  fontFamily: FONT,
  fontSize: 14,
  fontWeight: 600,
  color: TEXT,
  whiteSpace: 'nowrap' as const
};

export const MARKER = 24;
export const STATUS_COLOR: Record<SetupStep['status'], string> = {
  done: DONE,
  current: BRANDY,
  locked: '#565656'
};
export const DIVIDE = (
  <Box
    component="span"
    aria-hidden
    sx={{ flexShrink: 0, color: '#565656', mx: '10px', fontFamily: FONT, fontSize: 14 }}
  >
    |
  </Box>
);

/** A step's marker: a green tick when done, its number in brandy while it is open, a lock while it waits. */
export const StepMarker: React.FC<{ step: SetupStep; number: number }> = ({ step, number }) => {
  const color = STATUS_COLOR[step.status];
  return (
    <Box
      sx={{
        width: MARKER,
        height: MARKER,
        flexShrink: 0,
        borderRadius: '50%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        border: `1.5px solid ${color}`,
        bgcolor: step.status === 'done' ? DONE : step.status === 'current' ? 'rgba(247, 148, 32, 0.12)' : 'transparent',
        color: step.status === 'done' ? '#121212' : color,
        fontFamily: FONT,
        fontSize: 11.5,
        fontWeight: 700,
        lineHeight: 1
      }}
    >
      {step.status === 'done' && <CheckIcon sx={{ fontSize: 15 }} />}
      {step.status === 'current' && number}
      {step.status === 'locked' && <LockOutlinedIcon sx={{ fontSize: 12 }} />}
    </Box>
  );
};

export const stepStatusText = (step: SetupStep) => {
  if (step.showDetail) return step.detail;
  return step.status === 'current' ? (step.optional ? 'Optional' : 'To do') : step.detail;
};

/** One step as it reads in the checklist: its marker, its title, where it stands. */
export const StepLabel: React.FC<{ step: SetupStep; number: number }> = ({ step, number }) => (
  <Box sx={{ minWidth: 0, display: 'flex', alignItems: 'center', gap: '9px' }}>
    <StepMarker step={step} number={number} />
    <Box sx={{ minWidth: 0, display: 'flex', flexDirection: 'column', gap: '4px' }}>
      <Typography
        sx={{
          fontFamily: FONT,
          fontSize: 12.5,
          fontWeight: 600,
          lineHeight: 1.15,
          color: step.status === 'locked' ? TEXT_3 : TEXT,
          whiteSpace: 'nowrap'
        }}
      >
        {step.title}
      </Typography>
      <Typography
        sx={{
          fontFamily: FONT,
          fontSize: 10.5,
          fontWeight: 600,
          lineHeight: 1,
          whiteSpace: 'nowrap',
          color: STATUS_COLOR[step.status]
        }}
      >
        {stepStatusText(step)}
      </Typography>
    </Box>
  </Box>
);

/** Every step in a line, joined by rules that turn green as steps are done: what the stepper shows on hover. */
export const AllSteps: React.FC<{ steps: SetupStep[]; teamName: string }> = ({ steps, teamName }) => {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px', p: '4px 2px' }}>
      <Typography sx={{ fontFamily: FONT, fontSize: 13, fontWeight: 600, color: TEXT, textAlign: 'center' }}>
        {teamName} Setup
      </Typography>
      <Box
        component="ol"
        sx={{
          m: 0,
          p: 0,
          listStyle: 'none',
          display: 'flex',
          alignItems: 'center',
          // A phone: the steps one under another, without the rules between them.
          [MOBILE_SX]: {
            flexDirection: 'column',
            alignItems: 'flex-start',
            gap: '10px',
            '& > [aria-hidden]': { display: 'none' }
          }
        }}
      >
        {steps.map((step, i) => (
          <React.Fragment key={step.key}>
            {i > 0 && (
              <Box
                aria-hidden
                sx={{
                  flexShrink: 0,
                  width: 18,
                  height: '1.5px',
                  mx: '8px',
                  bgcolor: steps[i - 1].status === 'done' ? DONE : '#333333'
                }}
              />
            )}
            <Box component="li">
              <StepLabel step={step} number={i + 1} />
            </Box>
          </React.Fragment>
        ))}
      </Box>
    </Box>
  );
};

/**
 * The team's setup stepper, in the middle of the top bar: only the step the
 * team is on (brandy while it is open; green once everything is done), with
 * every step on hover. Small enough to drop later without the bar changing.
 */
export const SetupStepper: React.FC<{ steps: SetupStep[]; teamName: string }> = ({ steps, teamName }) => {
  const isMobileApp = useIsMobileApp();
  const index = steps.findIndex((s) => s.status === 'current');
  const complete = index === -1;
  const shown: SetupStep = complete
    ? {
        key: 'invite',
        title: 'Setup complete',
        description: '',
        status: 'done',
        detail: `${steps.length} of ${steps.length} done`
      }
    : steps[index];
  return (
    <DosenTooltip
      placement="bottom"
      // A phone has no hover: a tap shows every step, for a few seconds.
      enterTouchDelay={isMobileApp ? 0 : undefined}
      leaveTouchDelay={isMobileApp ? 4000 : undefined}
      // Information only: it never takes a click meant for the bar beneath it (Add Capability, Import Capability).
      componentsProps={{ tooltip: { sx: { maxWidth: 'none' } }, popper: { sx: { pointerEvents: 'none' } } }}
      title={<AllSteps steps={steps} teamName={teamName} />}
    >
      <Box
        data-testid="setup-stepper"
        aria-label={`${teamName} setup: ${shown.title}`}
        sx={{
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
          height: 40,
          px: '12px',
          borderRadius: '4px',
          border: `1px solid ${complete ? `${DONE}55` : 'rgba(247, 148, 32, 0.45)'}`,
          bgcolor: complete ? `${DONE}0F` : BRANDY_TINT,
          cursor: 'default'
        }}
      >
        <StepLabel step={shown} number={index + 1} />
        {!complete && (
          <Typography sx={{ ...LABEL, flexShrink: 0, pl: '10px', borderLeft: `1px solid ${LINE}`, lineHeight: '24px' }}>
            Step {index + 1} of {steps.length}
          </Typography>
        )}
      </Box>
    </DosenTooltip>
  );
};

/**
 * The team in the top bar. With several teams (an org admin) it is the
 * switcher, each team listed with its role count; with one (a team manager)
 * it is just the name. An org admin also finds the organization's shared sets
 * here, under Shared (Development, Standards), below the Teams.
 */
export interface SwitchChoice {
  id: string;
  name: string;
  meta: string;
  disabled?: boolean;
}

export const TeamSwitch: React.FC<{
  teams: SwitchChoice[];
  /** The org admin's shared sets: listed under their own heading, below the teams. */
  shared?: SwitchChoice[];
  value: string;
  onChange: (id: string) => void;
}> = ({ teams, shared = [], value, onChange }) => {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const current = [...teams, ...shared].find((t) => t.id === value);
  // The page's primary label: bold, white, capitals, in an outline.
  const text = {
    ...SECTION_TITLE,
    fontWeight: 700,
    color: TEXT,
    display: 'inline-flex',
    alignItems: 'center',
    height: 34,
    px: '12px',
    border: `1px solid ${TEXT}`,
    borderRadius: '4px'
  };
  if (teams.length + shared.length <= 1) {
    return (
      <Typography data-testid="team-switcher" sx={text}>
        {current?.name}
      </Typography>
    );
  }
  const item = (t: SwitchChoice) => (
    <MenuItem
      key={t.id}
      selected={t.id === value}
      // Not the viewer's team: listed, so they can see it exists, but it cannot be opened.
      disabled={t.disabled}
      onClick={() => {
        onChange(t.id);
        setAnchor(null);
      }}
      sx={{
        fontFamily: FONT,
        fontSize: 13,
        color: t.id === value ? BRANDY : TEXT,
        gap: '20px',
        '&.Mui-disabled': { opacity: 1, color: TEXT_3 }
      }}
    >
      <Box component="span" sx={{ flex: 1 }}>
        {t.name}
      </Box>
      <Box component="span" sx={{ fontSize: 11, color: TEXT_3, fontStyle: t.disabled ? 'italic' : 'normal' }}>
        {t.meta}
      </Box>
    </MenuItem>
  );
  const heading = (title: string) => (
    <ListSubheader
      key={title}
      disableSticky
      sx={{ ...LABEL, bgcolor: 'transparent', lineHeight: 1, pt: '12px', pb: '6px', color: TEXT_3 }}
    >
      {title}
    </ListSubheader>
  );
  return (
    <>
      <Box
        component="button"
        type="button"
        data-testid="team-switcher"
        aria-haspopup="listbox"
        aria-label={`Team: ${current?.name}. Change team`}
        onClick={(e: React.MouseEvent<HTMLElement>) => setAnchor(e.currentTarget)}
        sx={{
          ...text,
          gap: '2px',
          pr: '6px',
          borderColor: anchor ? BRANDY : TEXT,
          bgcolor: 'transparent',
          cursor: 'pointer',
          transition: 'border-color 0.15s',
          '&:hover': { borderColor: BRANDY }
        }}
      >
        {current?.name}
        <ExpandMoreIcon sx={{ fontSize: 18 }} />
      </Box>
      <Menu
        anchorEl={anchor}
        open={Boolean(anchor)}
        onClose={() => setAnchor(null)}
        PaperProps={{
          sx: {
            mt: '4px',
            minWidth: 240,
            maxHeight: 420,
            bgcolor: '#232323',
            border: `1px solid ${LINE}`,
            borderRadius: '4px',
            boxShadow: '0 12px 32px rgba(0,0,0,0.55)'
          }
        }}
      >
        {shared.length > 0 && heading('Teams')}
        {teams.map(item)}
        {shared.length > 0 && heading('Shared')}
        {shared.map(item)}
      </Menu>
    </>
  );
};

/** What a section says while it has nothing in it yet: centred, with what will be there and why. */
export const EmptyState: React.FC<{ title: string; body: React.ReactNode; children?: React.ReactNode }> = ({
  title,
  body,
  children
}) => (
  <Box
    sx={{
      flex: 1,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      textAlign: 'center',
      gap: '8px',
      py: '40px',
      px: '12px'
    }}
  >
    <Typography sx={{ fontFamily: FONT, fontSize: 15, fontWeight: 600, lineHeight: 1.3, color: TEXT }}>
      {title}
    </Typography>
    <Typography sx={{ ...BODY, fontSize: 12.5, maxWidth: 460 }}>{body}</Typography>
    {children && (
      <Box sx={{ mt: '8px', display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: '8px' }}>{children}</Box>
    )}
  </Box>
);

export const INPUT_SX = {
  flex: 1,
  minWidth: 0,
  height: 34,
  px: '12px',
  border: '1px solid #3A3A3A',
  borderRadius: '4px',
  bgcolor: '#121212',
  color: TEXT,
  fontFamily: FONT,
  fontSize: 13,
  outline: 'none',
  '&::placeholder': { color: '#565656' },
  '&:focus': { borderColor: BRANDY }
};

/**
 * What an add came to: true when added; false when refused for the reason the
 * form's `refused` gives (already there); otherwise the reason itself (the
 * backend's).
 */
export type AddResult = boolean | string;

/**
 * A one-line add form: a name and its button. Enter adds. The name stays until
 * the add has gone through; a refusal keeps it, with the reason beneath. Used
 * for roles and for capabilities.
 */
export const AddForm: React.FC<{
  label: string;
  placeholder: string;
  button: string;
  onAdd: (name: string) => AddResult | Promise<AddResult>;
  refused: (name: string) => string;
  /** Why nothing more can be added, when it can't: the button is disabled and says why on hover. */
  blocked?: string;
  maxLength?: number;
  onCancel?: () => void;
  autoFocus?: boolean;
  /** On a section's header bar: no wider than it needs, on one line, the reason beneath on the right. */
  compact?: boolean;
  children?: React.ReactNode;
}> = ({
  label,
  placeholder,
  button,
  onAdd,
  refused,
  blocked,
  maxLength,
  onCancel,
  autoFocus = false,
  compact = false,
  children
}) => {
  const [name, setName] = useState('');
  const [error, setError] = useState<string | null>(null);
  // An add on its way to the backend: the name is held and the button waits.
  const [adding, setAdding] = useState(false);
  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim() || blocked || adding) return;
    setAdding(true);
    const result = await onAdd(name);
    setAdding(false);
    if (result === true) setName('');
    else setError(typeof result === 'string' ? result : refused(name.trim()));
  };
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: '6px',
        ...(compact ? { flex: '0 1 auto', minWidth: 0, alignItems: 'flex-end' } : { flex: 1, minWidth: 260 }),
        ...(compact ? { [MOBILE_SX]: { flex: '1 1 100%', alignItems: 'stretch' } } : null)
      }}
    >
      <Box
        component="form"
        onSubmit={submit}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          flexWrap: compact ? 'nowrap' : 'wrap',
          [MOBILE_SX]: { flexWrap: 'wrap' }
        }}
      >
        <Box
          component="input"
          autoFocus={autoFocus}
          value={name}
          readOnly={adding}
          maxLength={maxLength}
          aria-label={label}
          placeholder={placeholder}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            setName(e.target.value);
            setError(null);
          }}
          onKeyDown={(e: React.KeyboardEvent) => {
            if (e.key === 'Escape' && onCancel) onCancel();
          }}
          sx={{
            ...INPUT_SX,
            maxWidth: compact ? 220 : 380,
            ...(compact ? { width: 220, flex: '0 1 220px' } : {}),
            ...(error ? { borderColor: '#FF6A55' } : {}),
            ...(compact ? { [MOBILE_SX]: { width: '100%', flex: '1 1 100%', maxWidth: 'none', height: 40 } } : null)
          }}
        />
        <ActionButton type="submit" primary disabled={!name.trim() || adding || Boolean(blocked)} reason={blocked}>
          {button}
        </ActionButton>
        {onCancel && <ActionButton onClick={onCancel}>Cancel</ActionButton>}
        {children}
      </Box>
      {error && <Typography sx={{ ...BODY, fontSize: 12, color: '#FF6A55' }}>{error}</Typography>}
    </Box>
  );
};

export const chipSx = (selected: boolean) => ({
  flexShrink: 0,
  height: 30,
  px: '12px',
  display: 'inline-flex',
  alignItems: 'center',
  gap: '8px',
  borderRadius: '4px',
  border: selected ? BRANDY_FRAME : '1px solid #3A3A3A',
  bgcolor: selected ? 'rgba(247, 148, 32, 0.12)' : 'transparent',
  color: selected ? TEXT : TEXT_2,
  fontFamily: FONT,
  fontSize: 13,
  fontWeight: selected ? 600 : 400,
  whiteSpace: 'nowrap',
  cursor: 'pointer',
  transition: 'border-color 0.15s, color 0.15s',
  '&:hover': { color: TEXT, borderColor: BRANDY }
});

/** Elliott's line at the top of the main section: where the role stands, and the one thing to do next. */
export const ElliottNote: React.FC<{ title: string; body?: string; children?: React.ReactNode; done?: boolean }> = ({
  title,
  body,
  children,
  done = false
}) => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'center',
      flexWrap: 'wrap',
      gap: '14px',
      p: '12px 14px',
      borderRadius: '4px',
      border: done ? `1px solid ${DONE}55` : BRANDY_FRAME,
      bgcolor: done ? `${DONE}0F` : BRANDY_TINT
    }}
  >
    {done && (
      <Box
        sx={{
          width: 32,
          height: 32,
          flexShrink: 0,
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: `${DONE}22`
        }}
      >
        <CheckIcon sx={{ fontSize: 17, color: DONE }} />
      </Box>
    )}
    <Box sx={{ flex: 1, minWidth: 260 }}>
      <Typography sx={{ fontFamily: FONT, fontSize: 13.5, fontWeight: 600, lineHeight: 1.3, color: TEXT }}>
        {title}
      </Typography>
      {body && <Typography sx={{ ...BODY, fontSize: 12.5, lineHeight: 1.45, mt: '2px' }}>{body}</Typography>}
    </Box>
    {children}
  </Box>
);

export const LEVEL_LABEL_WIDTH = 104;

// A capability's bar on a phone: the name fills the first row beside the number and the bar's buttons.
const HEAD_NAME_PHONE = { flex: '1 1 calc(100% - 110px)' };
// A small-capitals line on the bar (Shared With, a tag), whole and wrapping on a phone rather than cut.
const LABEL_WRAP_PHONE = {
  flexShrink: 1,
  minWidth: 0,
  maxWidth: 'none',
  whiteSpace: 'normal',
  overflow: 'visible',
  lineHeight: 1.35,
  py: '2px'
};

/** What a capability shows in place of its levels while Elliott is drafting them. */
export const DraftingLevels: React.FC = () => (
  <Box sx={{ p: '16px' }}>
    <ElliottWorking message="Elliott is drafting the levels..." size="inline" />
  </Box>
);

/** The page's shape while it loads: the top bar (team, role chips, actions), then capability bars. */
export const PageSkeleton: React.FC<{ card: object }> = ({ card }) => (
  <>
    <Box sx={{ ...card, flexShrink: 0, display: 'flex', alignItems: 'center', gap: '12px', minHeight: 60, px: '16px' }}>
      <Bone width={140} height={34} radius="4px" />
      <Bone width={110} height={30} radius="4px" />
      <Bone width={110} height={30} radius="4px" />
      <Box sx={{ flex: 1 }} />
      <Bone width={220} height={40} radius="4px" />
      <Bone width={130} height={34} radius="6px" />
    </Box>
    <Box sx={{ ...card, flex: 1, display: 'flex', flexDirection: 'column', gap: '10px', p: '16px' }}>
      <Bone width={260} height={22} radius="4px" />
      {[0, 1, 2, 3].map((i) => (
        <Bone key={i} height={40} radius="4px" />
      ))}
    </Box>
  </>
);

/**
 * One capability: its name on a bar (editable, with how many of its four
 * levels are set), opening onto a row for each level. Until the role's
 * capabilities are confirmed it is the bar alone: the level rows arrive with
 * Elliott's draft to review and edit, or empty to write where Elliott can't
 * draft yet.
 */
export const CapabilityRow: React.FC<{
  number: number;
  capability: SetupCapability;
  /** The role's capabilities are confirmed, so the levels are drafted and editable. */
  levelsOpen: boolean;
  drafting: boolean;
  /** Why its name can't be saved (blank, or another capability's), if it can't: the name is outlined in red. */
  problem?: string | null;
  /** The other roles of the team that share this capability, by name. */
  sharedWith?: string[];
  /** Added here under a name a team-mate role uses with levels of its own: import that one instead. */
  importFrom?: { roleName: string; onImport: () => void };
  /** Where it comes from (Role, Development, Standards): shown as a chip after its name. */
  source?: CapabilitySource;
  /** Set elsewhere (by the org admin, for this role): read here, never changed. Starts folded. */
  readOnly?: boolean;
  /** A line after the name ("Set by …"), in small capitals. */
  tag?: string;
  /** More on the bar, before its controls (who a shared capability applies to). */
  extra?: React.ReactNode;
  onRename?: (name: string) => void;
  onRemove?: () => void;
  onCriteria?: (level: CapabilityLevel, text: string) => void;
}> = ({
  number,
  capability,
  levelsOpen,
  drafting,
  problem,
  sharedWith: sharing = [],
  importFrom,
  source,
  readOnly = false,
  tag,
  extra,
  onRename,
  onRemove,
  onCriteria
}) => {
  // Open on its levels, set here or set by the org admin alike.
  const [expanded, setExpanded] = useState(true);
  // No level rows before the capabilities are confirmed, so nothing to open.
  const open = levelsOpen && expanded;
  const set = levelsSet(capability);
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', ...fadeInSx }}>
      {/* The same bar as a Capability Measures row on the dashboard. */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          minHeight: 40,
          pl: '12px',
          pr: '6px',
          border: BRANDY_FRAME,
          bgcolor: BRANDY_TINT,
          borderRadius: open ? '4px 4px 0 0' : '4px',
          // A phone: the number, the name and the bar's buttons on the first row; the chips,
          // what it is shared with and its levels count wrap onto the rows beneath, unclipped.
          [MOBILE_SX]: {
            flexWrap: 'wrap',
            rowGap: '4px',
            py: '6px',
            '& > *': { order: 1 },
            '& > .capability-head': { order: 0 }
          }
        }}
      >
        <Typography className="capability-head" sx={{ flexShrink: 0, fontFamily: FONT, fontSize: 14, color: TEXT_3 }}>
          {number}.
        </Typography>
        {readOnly ? (
          <Typography
            className="capability-head"
            sx={{
              // Where a role's own name field is, so what follows sits where it does on that row.
              flex: 1,
              minWidth: 0,
              fontFamily: FONT,
              fontSize: 14,
              color: TEXT,
              textTransform: 'uppercase',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              [MOBILE_SX]: { ...HEAD_NAME_PHONE, whiteSpace: 'normal', py: '5px' }
            }}
          >
            {capability.name}
          </Typography>
        ) : (
          <Box
            component="input"
            className="capability-head"
            value={capability.name}
            maxLength={MAX_NAME}
            aria-label={`Capability ${number} name`}
            aria-invalid={Boolean(problem)}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => onRename?.(e.target.value)}
            sx={{
              flex: 1,
              minWidth: 0,
              height: 30,
              px: '6px',
              ml: '-6px',
              border: `1px solid ${problem ? '#FF6A55' : 'transparent'}`,
              borderRadius: '4px',
              bgcolor: 'transparent',
              color: TEXT,
              fontFamily: FONT,
              fontSize: 14,
              textTransform: 'uppercase',
              outline: 'none',
              '&:hover': { borderColor: '#3A3A3A' },
              '&:focus': { borderColor: BRANDY, bgcolor: '#121212' },
              [MOBILE_SX]: HEAD_NAME_PHONE
            }}
          />
        )}
        {source && <SourceChip source={source} />}
        {/* As "Shared With" reads on a role's own. */}
        {tag && (
          <DosenTooltip title={tag} placement="bottom">
            <Typography
              sx={{
                ...LABEL,
                flexShrink: 1,
                minWidth: 0,
                maxWidth: 420,
                color: BRANDY,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                [MOBILE_SX]: LABEL_WRAP_PHONE
              }}
            >
              {tag}
            </Typography>
          </DosenTooltip>
        )}
        {extra}
        {importFrom && (
          <DosenTooltip
            title={`Use ${importFrom.roleName}'s ${capability.name} here: the same four levels, shared by both roles. Any levels written for this one are replaced.`}
            placement="bottom"
          >
            <Box
              component="button"
              type="button"
              onClick={importFrom.onImport}
              sx={{
                ...LABEL,
                flexShrink: 0,
                p: 0,
                border: 'none',
                bgcolor: 'transparent',
                color: BRANDY,
                cursor: 'pointer',
                whiteSpace: 'nowrap',
                '&:hover': { color: TEXT }
              }}
            >
              Import From {importFrom.roleName}
            </Box>
          </DosenTooltip>
        )}
        {sharing.length > 0 && (
          <DosenTooltip
            title={`One capability, the same for ${sharing.join(
              ', '
            )} and this role. Change its levels here and it becomes this role's own capability, under a new name; the others keep it as it was.`}
            placement="bottom"
          >
            <Typography
              sx={{
                ...LABEL,
                flexShrink: 0,
                maxWidth: 220,
                color: BRANDY,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                [MOBILE_SX]: LABEL_WRAP_PHONE
              }}
            >
              Shared With {sharing.join(', ')}
            </Typography>
          </DosenTooltip>
        )}
        {levelsOpen && (
          <Typography
            sx={{
              flexShrink: 0,
              fontFamily: FONT,
              fontSize: 11,
              whiteSpace: 'nowrap',
              color: set === LEVEL_ORDER.length ? DONE : TEXT_2
            }}
          >
            {set} of {LEVEL_ORDER.length} levels
          </Typography>
        )}
        {onRemove && !readOnly && (
          <DosenTooltip title="Remove capability" placement="bottom">
            <IconButton
              className="capability-head"
              aria-label={`Remove ${capability.name}`}
              onClick={onRemove}
              sx={{ width: 28, height: 28, p: 0, color: TEXT_3, '&:hover': { color: '#FF6A55' } }}
            >
              <DeleteOutlineIcon sx={{ fontSize: 17 }} />
            </IconButton>
          </DosenTooltip>
        )}
        {levelsOpen && (
          <IconButton
            className="capability-head"
            aria-label={open ? `Collapse ${capability.name}` : `Expand ${capability.name}`}
            aria-expanded={open}
            onClick={() => setExpanded((o) => !o)}
            sx={{ width: 28, height: 28, p: 0, color: TEXT }}
          >
            {open ? <ExpandLessIcon sx={{ fontSize: 20 }} /> : <ExpandMoreIcon sx={{ fontSize: 20 }} />}
          </IconButton>
        )}
      </Box>

      <Collapse in={open} unmountOnExit>
        <Box sx={{ border: `1px solid ${LINE}`, borderTop: 'none', borderRadius: '0 0 4px 4px', bgcolor: '#121212' }}>
          {drafting && <DraftingLevels />}
          {!drafting &&
            LEVEL_ORDER.map((level, i) => {
              // One marker per level (./levelRule.ts), shown while the levels can be edited.
              const problem = readOnly ? undefined : levelProblems(capability.criteria).find((p) => p.level === level)?.problem;
              return (
              <Box
                key={level}
                sx={{
                  display: 'flex',
                  alignItems: 'stretch',
                  ...(i > 0 ? { borderTop: '1px solid #1F1F1F' } : {}),
                  // A phone: the level's name over its criteria, so the criteria get the full width.
                  [MOBILE_SX]: { flexDirection: 'column' }
                }}
              >
                <Box
                  sx={{
                    width: LEVEL_LABEL_WIDTH,
                    flexShrink: 0,
                    display: 'flex',
                    alignItems: 'center',
                    px: '12px',
                    borderRight: `1px solid ${LINE}`,
                    [MOBILE_SX]: { width: 'auto', borderRight: 'none', pt: '10px' }
                  }}
                >
                  <Typography sx={{ ...LABEL, color: LEVEL_COLOR[level] }}>{LEVEL_NAME[level]}</Typography>
                </Box>
                <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
                <InputBase
                  multiline
                  fullWidth
                  value={capability.criteria[level] ?? ''}
                  placeholder={`What a ${LEVEL_NAME[level]} response looks like`}
                  readOnly={readOnly}
                  onChange={(e) => onCriteria?.(level, e.target.value)}
                  inputProps={{
                    'aria-label': `${capability.name}: ${LEVEL_NAME[level]} criteria`,
                    maxLength: MAX_CRITERIA
                  }}
                  sx={{
                    px: '12px',
                    py: '8px',
                    [MOBILE_SX]: { pt: '4px', pb: '10px', fontSize: 13 },
                    fontFamily: FONT,
                    fontSize: 12.5,
                    lineHeight: 1.5,
                    color: TEXT,
                    border: '1px solid transparent',
                    '&.Mui-focused': { borderColor: BRANDY },
                    '& textarea::placeholder': { color: '#565656', opacity: 1 }
                  }}
                />
                {problem && (
                  <Typography
                    role="status"
                    aria-label={`${capability.name}: ${LEVEL_NAME[level]} level rule`}
                    sx={{ px: '12px', pb: '8px', fontFamily: FONT, fontSize: 11.5, lineHeight: 1.4, color: '#E8A33D' }}
                  >
                    {problem}
                  </Typography>
                )}
                </Box>
              </Box>
              );
            })}
        </Box>
      </Collapse>
    </Box>
  );
};

/**
 * The role in view, on the main section's header: its name, editable in place
 * (Enter or leaving the field saves; a blank or a name another role in the
 * team has is refused and put back, and so is a name the save didn't keep),
 * and Delete, which asks once more before it goes. A role can be deleted only
 * until it is first confirmed (its levels; with only shared capabilities, its
 * capabilities), and never once launched.
 */
export const RoleHeader: React.FC<{
  role: SetupRole;
  /** Why the name can't be used (blank, or another role's in the team), or null when it can. */
  refusal: (name: string) => string | null;
  /** Saves the new name; resolves false when it was not saved (the save has said why). */
  onRename: (name: string) => Promise<boolean>;
  /** Deletes the role; resolves false when it was not deleted. */
  onDelete: () => Promise<boolean>;
  /** Shown after the name, before the space that pushes Delete to the right. */
  after?: React.ReactNode;
  /** On the right, before Delete: the section's actions (add, import, suggest). */
  actions?: React.ReactNode;
}> = ({ role, refusal, onRename, onDelete, after, actions }) => {
  const [name, setName] = useState(role.name);
  const [refused, setRefused] = useState<string | null>(null);
  const [confirming, setConfirming] = useState(false);
  const [deleting, setDeleting] = useState(false);
  useEffect(() => {
    setName(role.name);
    setRefused(null);
    setConfirming(false);
    setDeleting(false);
  }, [role.id, role.name]);
  const save = async () => {
    const unchanged = name.trim() === role.name;
    const why = unchanged ? null : refusal(name);
    if (unchanged || why) {
      setRefused(why);
      setName(role.name);
      return;
    }
    if (!(await onRename(name))) setName(role.name);
  };
  const remove = async () => {
    setDeleting(true);
    if (!(await onDelete())) setDeleting(false);
  };
  return (
    <>
      {/* As wide as the name, whatever its length: an unseen copy of it, in the same type, sets the width. */}
      <Box
        data-value={name || 'Role'}
        sx={{
          display: 'inline-grid',
          flexShrink: 1,
          minWidth: 0,
          maxWidth: '100%',
          ml: '-8px',
          '&::after': {
            content: 'attr(data-value)',
            gridArea: '1 / 1',
            visibility: 'hidden',
            whiteSpace: 'pre',
            overflow: 'hidden',
            px: '8px',
            border: '1px solid transparent',
            fontFamily: FONT,
            fontSize: 14,
            textTransform: 'uppercase'
          }
        }}
      >
        <Box
          component="input"
          value={name}
          maxLength={MAX_NAME}
          aria-label="Role name"
          size={1}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            setName(e.target.value);
            setRefused(null);
          }}
          onBlur={save}
          onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
            if (e.key === 'Enter') e.currentTarget.blur();
            if (e.key === 'Escape') setName(role.name);
          }}
          sx={{
            gridArea: '1 / 1',
            width: '100%',
            minWidth: 0,
            height: 30,
            px: '8px',
            border: '1px solid transparent',
            borderRadius: '4px',
            bgcolor: 'transparent',
            color: TEXT_2,
            fontFamily: FONT,
            fontSize: 14,
            textTransform: 'uppercase',
            outline: 'none',
            '&:hover': { borderColor: '#3A3A3A', color: TEXT },
            '&:focus': { borderColor: BRANDY, bgcolor: '#121212', color: TEXT }
          }}
        />
      </Box>
      {refused && <Typography sx={{ ...BODY, fontSize: 12, color: '#FF6A55', ml: '8px' }}>{refused}</Typography>}
      {after}
      <Box sx={{ flex: 1 }} />
      {actions}
      {canDeleteRole(role) &&
        (confirming ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', [MOBILE_SX]: { flexWrap: 'wrap' } }}>
            <Typography sx={{ ...BODY, fontSize: 12.5, color: TEXT }}>
              Delete {role.name}
              {role.capabilities.length > 0 ? ' and its capabilities' : ''}?
            </Typography>
            <ActionButton disabled={deleting} onClick={() => setConfirming(false)}>
              Cancel
            </ActionButton>
            <ActionButton primary disabled={deleting} onClick={remove}>
              Delete Role
            </ActionButton>
          </Box>
        ) : (
          <DosenTooltip title="Delete role" placement="bottom">
            <IconButton
              aria-label={`Delete ${role.name}`}
              onClick={() => setConfirming(true)}
              sx={{ width: 30, height: 30, p: 0, color: TEXT_3, '&:hover': { color: '#FF6A55' } }}
            >
              <DeleteOutlineIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </DosenTooltip>
        ))}
    </>
  );
};
